import axios from "axios";
import Config from "@config";

/**
 * API Utility - Refactored to use isolated axios instances.
 * This prevents header bleeding between different services (Core vs Payment Lambda)
 * and resolves CORS issues caused by incompatible global settings.
 */

// 1. Instance for Core Platform (Main API)
const coreInstance = axios.create({
  baseURL: Config.apiEndpoint,
});

// 2. Instance for Payment Lambda
const payInstance = axios.create({
  baseURL: Config.payEndpoint,
});

/**
 * Helper to determine which instance to use based on URL
 */
function getClient(url) {
  if (url.includes(Config.payEndpoint) || (typeof url === 'string' && url.includes('payments.ginastee.com'))) {
    return payInstance;
  }
  return coreInstance;
}

const api = {
  get: async function (url, params = {}) {
    return await handleRequest(url, params, "get");
  },
  delete: async function (url, params = {}) {
    return await handleRequest(url, params, "delete");
  },
  post: async function (url, params = {}) {
    return await handleRequest(url, params, "post");
  },
  patch: async function (url, params = {}) {
    return await handleRequest(url, params, "patch");
  },

  checkoutSessionToken: null,
  isSessionInitialized: false,
  sessionInitializationPromise: null,

  configure: async function () {
    // Warm up the session only once
    await this.ensureCheckoutSession();
  },

  ensureCheckoutSession: async function () {
    if (api.isSessionInitialized) {
      return api.checkoutSessionToken;
    }

    if (api.sessionInitializationPromise) {
      return await api.sessionInitializationPromise;
    }

    api.sessionInitializationPromise = this._initializeSession();
    
    try {
      const token = await api.sessionInitializationPromise;
      api.isSessionInitialized = true;
      return token;
    } catch (error) {
      api.sessionInitializationPromise = null;
      throw error;
    }
  },

  _initializeSession: async function () {
    try {
      // Sessions no longer expire (backend keeps them valid indefinitely) —
      // an existing token is always reused. Expiring them here surfaced
      // "session expired" failures on returning buyers and hurt conversion.
      const existingToken = localStorage.getItem("checkout_session_token");
      localStorage.removeItem("checkout_session_expires");

      if (existingToken) {
        this._applyToken(existingToken);
        console.log('Using existing checkout session token');
        return existingToken;
      }

      return await this.createCheckoutSession();
    } catch (error) {
      console.error('Error initializing checkout session:', error);
      return null;
    }
  },

  createCheckoutSession: async function () {
    try {
      const deviceData = this.collectDeviceData();
      console.log('Creating new checkout session...');

      // Call session creation on CORE instance
      const response = await coreInstance.post('checkout/create-session', deviceData);

      if (response.data && response.data.success) {
        const { token } = response.data;

        localStorage.setItem("checkout_session_token", token);

        this._applyToken(token);
        
        console.log('Checkout session created successfully');
        return token;
      }
      throw new Error('Failed to create checkout session');
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  },

  _applyToken: function (token) {
    const bearer = `Bearer ${token}`;
    api.checkoutSessionToken = token;
    
    // Apply to both instances to ensure authorized calls
    coreInstance.defaults.headers.common['Authorization'] = bearer;
    payInstance.defaults.headers.common['Authorization'] = bearer;
  },

  collectDeviceData: function () {
    return {
      timestamp: new Date().toISOString(),
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      navigator: {
        language: navigator.language,
        languages: navigator.languages,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: navigator.deviceMemory,
        maxTouchPoints: navigator.maxTouchPoints
      },
      timezone: {
        name: Intl.DateTimeFormat().resolvedOptions().timeZone,
        offset: new Date().getTimezoneOffset()
      },
      url: {
        href: window.location.href,
        search: window.location.search,
        hash: window.location.hash
      }
    };
  },

  resetSession: function () {
    api.isSessionInitialized = false;
    api.sessionInitializationPromise = null;
    api.checkoutSessionToken = null;
    
    localStorage.removeItem("checkout_session_token");
    localStorage.removeItem("checkout_session_expires");
    
    delete coreInstance.defaults.headers.common['Authorization'];
    delete payInstance.defaults.headers.common['Authorization'];
    
    console.log('Checkout session reset');
  }
};

async function handleRequest(url, params, type, isRetry = false) {
  const client = getClient(url);
  try {
    return await client[type](url, params);
  } catch (e) {
    // A 401 means the stored session token no longer exists server-side —
    // recover by minting a fresh session and replaying the call once
    // instead of failing the buyer's attempt.
    if (e.response && e.response.status === 401 && !isRetry) {
      console.warn('Authentication failed, recreating checkout session');
      api.resetSession();

      const token = await api.ensureCheckoutSession();

      if (token) {
        return await handleRequest(url, params, type, true);
      }
    }
    return e;
  }
}

export default api;
